import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { BellRing, ChevronRight, Download, Info, X } from 'lucide-react';
import TickerTape from '../components/layout/TickerTape';
import AdSlot from '../components/advertisement/AdSlot';
import FaqSection from '../components/common/FaqSection';
// Discussion section hidden for now
// import GmpDiscussion from '../components/gmp/GmpDiscussion';
import UpcomingStatCards from '../components/upcoming/UpcomingStatCards';
import PipelineTable from '../components/upcoming/PipelineTable';
import DrhpJourneySection from '../components/upcoming/DrhpJourneySection';
import ResearchReportsCard from '../components/upcoming/ResearchReportsCard';
import OpeningSoonSection from '../components/upcoming/OpeningSoonSection';
import { UPCOMING_FAQS } from '../data/upcomingIposData';
import { buildFilingsCsv, tabByKey } from '../utils/upcomingIpos';
import { formatMonthLabel, monthKeyOf, todayIso } from '../utils/ipoCalendar';
import { fetchCatalogIndex } from '../store/slices/ipoCatalogSlice';
import { toIsoDate } from '../utils/ipoCatalogAdapters';

const CATALOG_MAX_AGE_MS = 10 * 60 * 1000;
const NOTICE_MS = 4500;

const HEADINGS = {
  mainboard: 'Upcoming Mainboard IPOs Pipeline',
  sme: 'Upcoming SME IPOs Pipeline',
  confidential: 'Confidential IPO Filings Pipeline',
  approved: 'SEBI-Approved IPOs Ready to Launch',
};

// const DISCUSSION_COPY = {
//   headingId: 'upcoming-discussion-heading',
//   title: 'Community Discussion & Query Hub',
//   subtitle: 'Discuss DRHP filing timelines, valuation concerns and grey market speculation.',
//   placeholder: 'Write a comment or ask an IPO analyst…',
//   submitLabel: 'Post a Query',
// };

export const UpcomingIposPage = () => {
  const dispatch = useDispatch();
  const today = useMemo(() => todayIso(), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabByKey(searchParams.get('tab')).key;
  const [visibleRows, setVisibleRows] = useState([]);
  const [notice, setNotice] = useState(null);

  const catalog = useSelector((state) => state.ipoCatalog.index.records);

  // The catalog list, one request. The commented-out PipelineTable below is
  // the only thing on this page that needed a record per filing; restoring it
  // means restoring fetchIpoCatalog with it.
  useEffect(() => {
    dispatch(fetchCatalogIndex({ maxAgeMs: CATALOG_MAX_AGE_MS }));
  }, [dispatch]);

  // When the catalog was last refreshed, from the list's own timestamps
  const lastUpdatedIso = useMemo(
    () => catalog.map((item) => toIsoDate(item.updated_at) || toIsoDate(item.drhp_date)).filter(Boolean).sort().pop(),
    [catalog]
  );
  const LAST_UPDATED = lastUpdatedIso ? formatMonthLabel(monthKeyOf(lastUpdatedIso)) : '—';

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${HEADINGS[tab]} — DRHP Tracker | Alambrix IPO`;
    return () => {
      document.title = previousTitle;
    };
  }, [tab]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), NOTICE_MS);
    return () => clearTimeout(timer);
  }, [notice]);

  const changeTab = (key) => setSearchParams(key === 'mainboard' ? {} : { tab: key }, { replace: true });

  const handleExport = () => {
    // BOM so spreadsheet apps read ₹ correctly
    const blob = new Blob(['﻿', buildFilingsCsv(visibleRows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `upcoming-ipos-${tab}-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <>
      <TickerTape />

      <main className="flex-1 bg-slate-50/70">
        <div className="w-full max-w-[1680px] mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28 pb-12 sm:pb-16 space-y-6 sm:space-y-7">
          {/* Sponsored banner (GET /api/ads): dedicated slot first, then the home leaderboard */}
          <AdSlot slotId={['upcoming-ipos-top', 'leaderboard']} />

          {/* Breadcrumb + header */}
          <div>
            <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-xs text-slate-500 font-medium overflow-x-auto whitespace-nowrap">
              <Link to="/" className="hover:text-[#008080] transition-colors">
                Home
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <Link to="/#drhp-filings-section" className="hover:text-[#008080] transition-colors">
                SEBI DRHP Filings
              </Link>
              <ChevronRight className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="text-[#008080] font-semibold">{HEADINGS[tab]}</span>
            </nav>

            <div className="mt-4 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
              <div className="max-w-3xl">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-[11px] font-bold text-emerald-700">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Updated {LAST_UPDATED} Database
                </span>
                <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">{HEADINGS[tab]}</h1>
                <p className="mt-2 text-sm text-slate-500 leading-relaxed">
                  Explore companies that have filed their Draft Red Herring Prospectus (DRHP) with{' '}
                  <strong className="text-slate-700">SEBI / stock exchanges</strong>. Track the review lifecycle,
                  observation letters, issue size composition and expected listing timeframes.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={!visibleRows.length}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl border border-slate-200 bg-white text-xs font-bold text-slate-700 hover:border-slate-300 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  Export DRHP Data (CSV)
                </button>
                {/* <button
                  type="button"
                  onClick={() => setNotice('Pipeline alerts are coming soon. Star companies to track them on this device for now.')}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-[#008080] hover:bg-[#006666] text-xs font-bold text-white shadow-sm transition-colors"
                >
                  <BellRing className="w-3.5 h-3.5" />
                  Set Pipeline Alerts
                </button> */}
              </div>
            </div>
          </div>

          {/* Scheduled issues with confirmed bidding dates (GET /api/market/ipos/upcoming) */}
          <OpeningSoonSection today={today} />

          {/* <UpcomingStatCards stats={STATS} /> */}

          {/* <PipelineTable
            filings={DRHP_FILINGS}
            tab={tab}
            onTabChange={changeTab}
            today={today}
            onVisibleChange={setVisibleRows}
            onViewDocument={(filing) =>
              setNotice(`The ${filing.company} draft prospectus will be available once filings are connected.`)
            }
          /> */}

          <DrhpJourneySection />

          <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-5 sm:gap-6 items-start">
            <div className="space-y-5 sm:space-y-6 min-w-0">
              <FaqSection
                id="upcoming-faq"
                eyebrow="Help Center"
                title="Frequently Asked Questions on Upcoming IPOs"
                faqs={UPCOMING_FAQS}
              />
              {/* Discussion section hidden for now: <GmpDiscussion initialThreads={[]} copy={DISCUSSION_COPY} /> */}

            </div>
            <aside className="space-y-5 sm:space-y-6" aria-label="Partners and reports">
              {/* Partner ad (GET /api/ads): dedicated slot first, then the in-feed poster */}
              <AdSlot slotId={['upcoming-ipos-sidebar', 'infeed']} variant="compact" />
              <ResearchReportsCard />
            </aside>
          </div>
        </div>

        {notice && (
          <div
            role="status"
            className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-md flex items-start gap-2 rounded-xl bg-slate-900 text-white text-xs px-4 py-3 shadow-elevated"
          >
            <Info className="w-4 h-4 flex-shrink-0 text-teal-300" />
            <span className="flex-1">{notice}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss" className="text-slate-400 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </main>
    </>
  );
};

export default UpcomingIposPage;
